import React, { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components/macro';
import { previewFileActions } from '../../store/actions';
import { Fab } from '@material-ui/core';
import FullscreenOverlay from '../overlays/FullscreenOverlay';
import { createSelector } from '@reduxjs/toolkit';
import {
  ChevronLeftRounded,
  ChevronRightRounded,
} from '@material-ui/icons';

const previewFileSelector = (state) => state.previewFile;
const hasMultiPagesSelector = createSelector(
  previewFileSelector,
  (storeState) => !!storeState.allPages,
);
const currentPreviewPageSelector = createSelector(
  previewFileSelector,
  hasMultiPagesSelector,
  ({ url, mimeType, allPages, currentIndex }, hasMulti) => {
    if (!hasMulti) return { url, mimeType };
    else if (currentIndex < allPages.length) {
      return allPages[currentIndex];
    } else {
      throw Error(`invalid currentIndex ${currentIndex}`);
    }
  },
);

const hasNextSelector = createSelector(
  previewFileSelector,
  hasMultiPagesSelector,
  ({ url, mimeType, allPages, currentIndex }, hasMulti) => {
    if (!hasMulti) return false;
    if (currentIndex < allPages.length - 1) return true;
    return false;
  },
);

const hasPrevSelector = createSelector(
  previewFileSelector,
  hasMultiPagesSelector,
  ({ url, mimeType, allPages, currentIndex }, hasMulti) => {
    if (!hasMulti) return false;
    if (currentIndex > 0) return true;
    return false;
  },
);

interface PreviewPdfProps {}

const PreviewFile: React.FC<PreviewPdfProps> = () => {
  const { url, mimeType } = useSelector(currentPreviewPageSelector);
  const hasMultiPage = useSelector(hasMultiPagesSelector);
  const hasPrev = useSelector(hasPrevSelector);
  const hasNext = useSelector(hasNextSelector);

  const dispatch = useDispatch();

  /****************** start: check for ESC keyup ********************/

  const _handleKeyUp = useRef((e) => {
    if (!e) e = window.event;
    const keyCode = e.keyCode || e.which;
    if (keyCode === '27') {
      // check if press esc
      // dispatch(previewFileActions.close());
    }
  });

  useEffect(() => {
    const handlerKeyup = _handleKeyUp.current;
    document.addEventListener('keyup', handlerKeyup);
    return () => {
      document.removeEventListener('keyup', handlerKeyup);
    };
  }, []);

  /****************** end: check for ESC keyup ********************/

  const handleClose = () => {
    dispatch(previewFileActions.close());
  };

  const handleNext = (e) => {
    dispatch(previewFileActions.goNextPage());
  };
  const handlePrev = (e) => {
    dispatch(previewFileActions.goPrevPage());
  };

  // useEffect(() => {
  //   console.debug(`hasMultiPage: ${hasMultiPage}`);
  // }, [hasMultiPage]);
  if (!url) return null;
  return (
    <FullscreenOverlay
      open={Boolean(url)}
      noAppBar
      handleClose={handleClose}
      closeButtonStyle={{
        color: 'white',
        marginRight: '-5px',
        zIndex: 1,
      }}
    >
      <SObjectFileWrapper>
        {/* note embed to make it works with old browser */}
        <object data={url} type={mimeType}>
          <embed src={url} type={mimeType} />
        </object>
        {hasMultiPage && (
          <SLeftButton disabled={!hasPrev} onClick={handlePrev}>
            <ChevronLeftRounded />
          </SLeftButton>
        )}
        {hasMultiPage && (
          <SRightButton disabled={!hasNext} onClick={handleNext}>
            <ChevronRightRounded />
          </SRightButton>
        )}
      </SObjectFileWrapper>
    </FullscreenOverlay>
  );
};

export default PreviewFile;

const SObjectFileWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  & > object {
    width: 100%;
    height: 100%;
  }
`;
const SLeftButton = styled(Fab)`
  &.MuiFab-root {
    position: absolute;
    top: 45%;
    left: 5rem;
    z-index: 1;
  }
`;
const SRightButton = styled(Fab)`
  &.MuiFab-root {
    position: absolute;
    top: 45%;
    right: 5rem;
    z-index: 1;
  }
`;
